import { Navigate, Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
    CatalogEntityPage,
    CatalogIndexPage,
    catalogPlugin,
} from '@backstage/plugin-catalog';
import {
    CatalogImportPage,
    catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
    TechDocsIndexPage,
    techdocsPlugin,
    TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import { microsoftAuthApiRef } from '@backstage/core-plugin-api';

import {
    AlertDisplay,
    OAuthRequestDialog,
    SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { ExplorePage } from '@backstage-community/plugin-explore';
import { UnifiedThemeProvider } from '@backstage/theme';
import { explorePage } from './components/explore/ExplorePage';
import { LightIcon } from './assets/icons/douglasIcons.tsx';

import './themes/styles.css';
import { douglasDark, douglasLight } from './themes/themes.ts';

const app = createApp({
    apis,
    bindRoutes({ bind }) {
        bind(catalogPlugin.externalRoutes, {
            viewTechDoc: techdocsPlugin.routes.docRoot,
        });
        bind(apiDocsPlugin.externalRoutes, {
            registerApi: catalogImportPlugin.routes.importPage,
        });
        bind(orgPlugin.externalRoutes, {
            catalogIndex: catalogPlugin.routes.catalogIndex,
        });
    },
    components: {
        SignInPage: props => (
            <SignInPage
                {...props}
                auto
                provider={{
                    id: 'azure-auth-provider',
                    title: 'Azure',
                    message: 'Sign in using Microsoft Azure.',
                    apiRef: microsoftAuthApiRef,
                }}
            />
        ),
    },
    themes: [
        {
            id: 'light',
            title: 'Light theme',
            variant: 'light',
            icon: <LightIcon />,
            Provider: ({ children }) => <UnifiedThemeProvider theme={douglasLight} children={children} />,
        },
        {
            id: 'dark',
            title: 'Dark theme',
            variant: 'dark',
            icon: <LightIcon />,
            Provider: ({ children }) => <UnifiedThemeProvider theme={douglasDark} children={children} />,
        },
    ],
});

const routes = (
    <FlatRoutes>
        <Route path="/" element={<Navigate to="catalog" />} />
        <Route path="/catalog" element={<CatalogIndexPage />} />
        <Route path="/catalog/:namespace/:kind/:name" element={<CatalogEntityPage />}>
            {entityPage}
        </Route>
        <Route path="/docs" element={<TechDocsIndexPage />} />
        <Route path="/docs/:namespace/:kind/:name/*" element={<TechDocsReaderPage />}>
            <TechDocsAddons>
                <ReportIssue />
            </TechDocsAddons>
        </Route>
        <Route path="/api-docs" element={<ApiExplorerPage />} />
        <Route
            path="/catalog-import"
            element={
                <RequirePermission permission={catalogEntityCreatePermission}>
                    <CatalogImportPage />
                </RequirePermission>
            }
        />
        <Route path="/search" element={<SearchPage />}>
            {searchPage}
        </Route>
        <Route path="/settings" element={<UserSettingsPage />} />
        <Route path="/catalog-graph" element={<CatalogGraphPage />} />
        <Route path="/explore" element={<ExplorePage />}>
            {explorePage}
        </Route>
    </FlatRoutes>
);

export default app.createRoot(
    <>
        <AlertDisplay />
        <OAuthRequestDialog />
        <AppRouter>
            <Root>{routes}</Root>
        </AppRouter>
    </>,
);
